/* TODO(ASIMO-2105): cherrera2@ to remove once experiment is over regardless of outcome */
import {
	setGoogleDriveConnectionStatus,
	setOneDriveConnectionStatus,
} from '../storage/thirdPartyConnectionsStatusStorage';

/**
 * Represents the connection details for a third-party data source integration
 */
export type DataSourceConnectionDetails = {
	/** Unique identifier for the data source integration */
	integrationId: string;
	/** Whether the user has completed OAuth authentication for this data source */
	isUserOAuthed: boolean;
	/** URL for initiating OAuth flow for this data source connection */
	outboundAuthUrl: string;
	/** Identifier for the data source provider */
	providerId: string;
	/** Type of data source connector (e.g., 'google-drive-connector') */
	type: string;
};

const GOOGLE_DRIVE_CONNECTORS_SET = new Set([
	'google-drive-connector',
	'smartlinks-connector-gdrive',
]);

const ONEDRIVE_CONNECTORS_SET = new Set([
	'onedrive-connector',
	'sharepoint-connector',
	'smartlinks-connector-onedrive',
	'smartlinks-connector-sharepoint',
]);

interface ThirdPartyConnectorMapping {
	connectorSet: Set<string>;
	storageStatusSetter: (connectionDetails: DataSourceConnectionDetails | undefined) => void;
}

enum ThirdPartyConnector {
	GOOGLE_DRIVE = 'google_drive',
	ONEDRIVE = 'onedrive',
}

const THIRD_PARTY_CONNECTORS_MAPPING: Record<ThirdPartyConnector, ThirdPartyConnectorMapping> = {
	[ThirdPartyConnector.GOOGLE_DRIVE]: {
		connectorSet: GOOGLE_DRIVE_CONNECTORS_SET,
		storageStatusSetter: setGoogleDriveConnectionStatus,
	},
	[ThirdPartyConnector.ONEDRIVE]: {
		connectorSet: ONEDRIVE_CONNECTORS_SET,
		storageStatusSetter: setOneDriveConnectionStatus,
	},
};

/**
 * Fetches third-party data source connection details for a given cloud instance
 * @param cloudId - The Atlassian cloud instance ID
 * @returns Promise resolving to an array of data source connection details, or empty array on error
 */
const getConnectedDataSourcesForCloudId = async (
	cloudId: string,
): Promise<Array<DataSourceConnectionDetails>> => {
	let dataSourceConnectionDetails: Array<DataSourceConnectionDetails> = [];
	try {
		const response = await fetch(
			`/gateway/api/third-party-configuration/connected-data-sources/${cloudId}`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'include',
			},
		);
		if (response.ok) {
			dataSourceConnectionDetails = await response.json();
		}
	} catch (err: any) {
		console.warn('Error fetching third party configuration in link picker', err);
	}
	return dataSourceConnectionDetails;
};

/**
 * Checks if the user has authorized the third party connector integration
 * and updates local storage with the connection status
 * @param cloudId - The Atlassian cloud instance ID (defaults to empty string)
 * @param connector - The third party connector type to check
 * @returns Promise resolving to true if third party connector is found and authorized, false otherwise
 */
export const checkIfUserHasAuthorizedThirdPartyConnector = async (
	cloudId = '',
	connector: ThirdPartyConnector,
): Promise<boolean> => {
	if (!cloudId) {
		console.warn('No cloudId provided in link picker');
		return false;
	}

	const { connectorSet, storageStatusSetter } = THIRD_PARTY_CONNECTORS_MAPPING[connector];

	const connectedDataSources = await getConnectedDataSourcesForCloudId(cloudId);
	const matchingConnections = (connectedDataSources || []).filter((dataSource) =>
		connectorSet.has(dataSource.type),
	);

	// Find the first authorized connection, or fall back to the first available connection.
	const authorizedConnection = matchingConnections.find((connection) => connection.isUserOAuthed);
	const selectedConnection = authorizedConnection || matchingConnections[0] || undefined;

	storageStatusSetter(selectedConnection);

	return Boolean(authorizedConnection);
};

export const checkIfUserHasAuthorizedGoogleDriveConnector = async (
	cloudId = '',
): Promise<boolean> =>
	checkIfUserHasAuthorizedThirdPartyConnector(cloudId, ThirdPartyConnector.GOOGLE_DRIVE);

export const checkIfUserHasAuthorizedOneDriveConnector = async (cloudId = ''): Promise<boolean> =>
	checkIfUserHasAuthorizedThirdPartyConnector(cloudId, ThirdPartyConnector.ONEDRIVE);
